import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Search,
  UtensilsCrossed,
  X,
} from "lucide-react";
import FoodOrderItems from "./FoodOrderItems";
import FoodOrderTrackingModal from "./FoodOrderTrackingModal";
import FoodOrderCancelDialog from "./FoodOrderCancelDialog";
import ChatPanel from "../../../../ecommerce/globalcomponents/ChatPanel/ChatPanel";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import {
  fetchMyOrders,
  fetchOrderById,
} from "../../../../Redux/custmoer_orders/slice";
import {
  CustomerPurchaseType,
  Order,
} from "../../../../Redux/custmoer_orders/types";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import "./FoodOrdersPage.css";

const FoodOrderList = () => {
  const dispatch = useAppDispatch();
  const { orders, totalPages, page, limit, loading, error } = useAppSelector(
    (state: RootState) => state.customerOrders,
  );

  const [currentLimit, setCurrentLimit] = useState(limit || 20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [paymentStatus, setPaymentStatus] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Tracking modal / cancel dialog / chat state
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Order | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatOrder, setChatOrder] = useState<Order | null>(null);

  const buildQuery = (pageNum: number) => ({
    page: pageNum,
    limit: currentLimit,
    purchaseType: CustomerPurchaseType.FOOD,
    search: debouncedSearch || undefined,
    startDate: dateRange.from
      ? new Date(dateRange.from).toISOString()
      : undefined,
    endDate: dateRange.to ? new Date(dateRange.to).toISOString() : undefined,
    paymentStatus: paymentStatus || undefined,
    deliveryStatus: deliveryStatus || undefined,
    sortBy: sortBy || undefined,
    sortOrder: sortOrder || undefined,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    dispatch(fetchMyOrders(buildQuery(page || 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    currentLimit,
    debouncedSearch,
    dateRange,
    paymentStatus,
    deliveryStatus,
    sortBy,
    sortOrder,
  ]);

  const handlePageChange = (newPage: number) => {
    dispatch(fetchMyOrders(buildQuery(newPage)));
  };

  const openTracking = (order: Order) => {
    const orderId = order.orderId || (order as any).id;
    if (orderId) {
      dispatch(fetchOrderById(orderId));
    }
    setTrackingOpen(true);
  };

  const openChat = (order: Order | null) => {
    setChatOrder(order);
    setChatOpen(true);
  };

  return (
    <section className="food-orders-page">
      <div className="food-orders-shell">
        <header className="food-orders-header">
          <div className="food-orders-heading">
            <span className="food-orders-heading-icon">
              <UtensilsCrossed size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="food-orders-kicker">FOOD & DINING</span>
              <h1>Food Orders</h1>
              <p>
                Track deliveries, reorder favourites and manage restaurant
                order activity.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="food-orders-help"
            onClick={() => openChat(null)}
          >
            <CircleHelp size={17} strokeWidth={2} />
            <span>Food order help</span>
          </button>
        </header>

        <div className="food-orders-toolbar">
          <select
            className="food-orders-filter-select"
            value={currentLimit}
            onChange={(e) => setCurrentLimit(Number(e.target.value))}
            aria-label="Orders per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option key={val} value={val}>
                Last {val} Entries
              </option>
            ))}
          </select>

          <select
            className="food-orders-filter-select"
            value={deliveryStatus}
            onChange={(e) => setDeliveryStatus(e.target.value)}
            aria-label="Delivery status"
          >
            <option value="">All Delivery Status</option>
            <option value="PENDING">Pending</option>
            <option value="IN_TRANSIT">In Transit</option>
            <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
            <option value="DELIVERED">Delivered</option>
            <option value="NOT_DELIVERED">Not Delivered</option>
          </select>

          <select
            className="food-orders-filter-select"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
            aria-label="Payment status"
          >
            <option value="">All Payment Status</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="REFUNDED">Refunded</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <select
            className="food-orders-filter-select"
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [by, order] = e.target.value.split("-");
              setSortBy(by);
              setSortOrder(order as "ASC" | "DESC");
            }}
            aria-label="Sort orders"
          >
            <option value="createdAt-DESC">Newest First</option>
            <option value="createdAt-ASC">Oldest First</option>
          </select>

          <div className="food-orders-date-range">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <span className="food-orders-count">{orders.length} orders</span>

          <div className="food-orders-search">
            <Search size={18} strokeWidth={2} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search restaurant, food or order ID"
              aria-label="Search food orders"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        <div className="food-orders-list">
          {loading ? (
            <div className="food-orders-empty">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="food-orders-empty">
              <UtensilsCrossed size={34} strokeWidth={1.8} />
              <strong>Something went wrong</strong>
              <span>{error}</span>
            </div>
          ) : (
            <FoodOrderItems
              orders={orders}
              onTrack={openTracking}
              onCancel={(order) => setCancelTarget(order)}
              onChat={openChat}
            />
          )}
        </div>

        {!loading && !error && totalPages > 1 && (
          <footer className="food-orders-pagination">
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <div>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft size={18} strokeWidth={2} />
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() =>
                  handlePageChange(Math.min(totalPages, page + 1))
                }
                aria-label="Next page"
              >
                <ChevronRight size={18} strokeWidth={2} />
              </button>
            </div>
          </footer>
        )}
      </div>

      <FoodOrderTrackingModal
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        onChat={() => {
          setTrackingOpen(false);
          openChat(null);
        }}
      />

      <FoodOrderCancelDialog
        open={Boolean(cancelTarget)}
        orderId={
          cancelTarget
            ? cancelTarget.orderId || (cancelTarget as any).id
            : null
        }
        orderLabel={cancelTarget?.orderNo}
        onClose={() => setCancelTarget(null)}
        onCancelled={() =>
          dispatch(fetchMyOrders(buildQuery(page || 1)))
        }
      />

      <ChatPanel
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatOrder(null);
        }}
        order={chatOrder}
      />
    </section>
  );
};

export default FoodOrderList;
